// Bir ekip veya kisi icin dagilim karti. CRM StatsCard pattern'ine hizali:
// beyaz kart, 16px radius, ince gri border, 1-2px shadow, hover'da yumusak
// translate + shadow. Icon box 42x42, gradient soft palette (statsCardStyles
// deseninin ayni). Icerik: entity header + toplam is + iss turu bar +
// iss durumu bar.

import { Card, CardContent, Box, Typography, Chip, Divider, useTheme, alpha } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DistributionBarChart from './DistributionBarChart';

const JOB_TYPE_LABELS = {
  AR_GE: 'AR-GE Merkezi',
  TASARIM_MERKEZI: 'Tasarım Merkezi',
  TEKNOPARK: 'Teknopark',
  TUBITAK: 'TÜBİTAK',
  ULUSLARARASI: 'Uluslararası',
  DIGER: 'Diğer',
};

const JOB_STATUS_LABELS = {
  YAZIMDA: 'Yazımda',
  DEGERLENDIRMEDE: 'Değerlendirmede',
  ONAYLANDI: 'Onaylandı',
  DEVRALINDI: 'Devralındı',
  ORTAK_BULUNDU: 'Ortak Bulundu',
  REDDEDILDI: 'Reddedildi',
  KAPATILDI: 'Kapatıldı',
  TAMAMLANDI: 'Tamamlandı',
  DANISMANLIK_SONLANDIRILDI: 'Danışmanlık Sonlandırıldı',
};

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: '#626b78', mb: 1,
  }}>
    {children}
  </Typography>
);

const DistributionCard = ({ entity, personCount, byType, byStatus, total, teamName }) => {
  const theme = useTheme();
  const isTeam = entity.kind === 'team';
  const Icon = isTeam ? GroupsIcon : PersonIcon;
  const accent = isTeam ? theme.palette.primary.main : (theme.palette.secondary?.main || '#7c5cf0');
  const accentSoft = alpha(accent, 0.12);

  // Status renk semantigi CRM StatsCard palette'iyle uyumlu.
  const statusColorMap = {
    YAZIMDA:         '#e0a106',
    DEGERLENDIRMEDE: '#0ea5e9',
    ONAYLANDI:       '#18a558',
    DEVRALINDI:      '#7c5cf0',
    ORTAK_BULUNDU:   '#3a5af0',
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '16px',
        background: '#ffffff',
        border: '1px solid #e8eaee',
        boxShadow: '0 1px 2px rgba(20,30,55,.04)',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{
        p: '20px !important',
        '&:last-child': { pb: '20px !important' },
        display: 'flex', flexDirection: 'column', gap: 2.25,
      }}>
        {/* Header: icon + entity ismi + chip'ler */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '12px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${accentSoft}, ${alpha(accent, 0.04)})`,
            border: `1px solid ${accentSoft}`,
            color: accent,
          }}>
            <Icon sx={{ fontSize: '1.35rem' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: '15px', fontWeight: 700, color: '#0f1424',
              letterSpacing: '-0.2px', lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {entity.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
              {personCount != null && (
                <Chip
                  onClick={() => {}}
                  icon={<GroupsIcon sx={{ fontSize: 13 }} />}
                  label={`${personCount} kişi`}
                  size="small" variant="outlined"
                  sx={{
                    height: 24, fontSize: 11.5, fontWeight: 600,
                    '& .MuiChip-label': { px: 1 },
                    '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
                  }}
                />
              )}
              {teamName && (
                <Chip
                  onClick={() => {}}
                  label={teamName}
                  size="small" variant="outlined"
                  sx={{
                    height: 24, fontSize: 11.5, fontWeight: 600,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Toplam is — sag ust badge, StatsCard.value miniature */}
          <Box sx={{
            textAlign: 'right', flexShrink: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
          }}>
            <Typography sx={{
              fontSize: '26px', fontWeight: 800, color: accent,
              lineHeight: 1, letterSpacing: '-0.5px',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {total}
            </Typography>
            <Box sx={{
              mt: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.4,
              px: 0.85, py: 0.25, borderRadius: '999px',
              background: accentSoft, color: accent,
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.03em',
            }}>
              <WorkOutlineIcon sx={{ fontSize: 11 }} />
              TOPLAM İŞ
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#eef0f3' }} />

        {/* İş türü bazında */}
        <Box>
          <SectionLabel>İş Türü Bazında</SectionLabel>
          <DistributionBarChart data={byType} labelMap={JOB_TYPE_LABELS} emptyText="İş yok" />
        </Box>

        {/* İş durumu bazında */}
        <Box>
          <SectionLabel>İş Durumu Bazında</SectionLabel>
          <DistributionBarChart
            data={byStatus}
            labelMap={JOB_STATUS_LABELS}
            colorMap={statusColorMap}
            emptyText="İş yok"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default DistributionCard;
